// src/Dashboard/pages/user/DashboardUser/components/FavoriteTechniquesPanel.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaStar } from "react-icons/fa";
import { toast } from "react-toastify";

import { getUserFavorites } from "../../../../../services/usersTechniquesServices";
import { getAllTechniques } from "../../../../../services/techniquesServices";
import { toAbsoluteTechniquePhotoUrl } from "../../../../../services/techniquesPhotoServices";

import EmptyState from "./EmptyState";
import { extractTechniqueId, uniqueIds } from "../utils/dashboardUserUtils";

export default function FavoriteTechniquesPanel({ userId, onCounts }) {
  const [loading, setLoading] = useState(true);
  const [techniques, setTechniques] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const prevCountRef = useRef(-1);

  const load = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);

      const [favorites, allTechniques] = await Promise.all([
        getUserFavorites(userId).catch(() => []),
        getAllTechniques().catch(() => []),
      ]);

      setFavoriteIds(uniqueIds(favorites, extractTechniqueId));
      setTechniques(Array.isArray(allTechniques) ? allTechniques : []);
    } catch (error) {
      console.error("[FavoriteTechniquesPanel] load error:", error);
      toast.error("Impossible de charger les techniques favorites.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!onCounts) return;
    if (prevCountRef.current === favoriteIds.length) return;

    prevCountRef.current = favoriteIds.length;
    onCounts({ total: favoriteIds.length });
  }, [favoriteIds.length, onCounts]);

  const favorites = useMemo(() => {
    if (!favoriteIds.length) return [];

    const map = new Map(techniques.map((technique) => [Number(technique.id), technique]));

    return favoriteIds.map((id) => map.get(Number(id))).filter(Boolean);
  }, [favoriteIds, techniques]);

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="h-32 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  if (!favorites.length) {
    return (
      <EmptyState
        icon={FaStar}
        title="Aucune technique favorite"
        description="Ajoutez des techniques en favoris depuis la bibliothèque technique pour les retrouver ici."
      />
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {favorites.map((technique) => (
        <article
          key={technique.id}
          className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
        >
          <div className="flex gap-4 p-4">
            <img
              src={
                toAbsoluteTechniquePhotoUrl(technique.image1_url) ||
                "/img/no-image.jpg"
              }
              alt={technique.nom || "Technique"}
              className="h-24 w-24 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
              loading="lazy"
              onError={(event) => {
                event.currentTarget.src = "/img/no-image.jpg";
              }}
            />

            <div className="min-w-0">
              <h3 className="font-bold text-slate-900 dark:text-white">
                {technique.nom || "—"}
              </h3>

              {technique.traduction && (
                <p className="mt-0.5 text-xs italic text-slate-500 dark:text-slate-400">
                  {technique.traduction}
                </p>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-violet-100 px-2.5 py-1 text-xs font-bold text-violet-700 dark:bg-violet-900/30 dark:text-violet-200">
                  {technique.famille || "Famille non définie"}
                </span>
                <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200">
                  {technique.ceinture || "Ceinture non définie"}
                </span>
              </div>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}